import { processingResultSelectors } from '@processingResults/redux';
import { useAppDispatch } from '@core/redux/hooks';
import { getScanHelperColor } from '@scans/helper';
import { scanActions, scanSelectors } from '@scans/redux';
import { jsonToEuler, jsonToVector } from '@viewer3D/helper';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { Euler, Vector3 } from 'three';
import { useSelector } from 'react-redux';
import type { RootState } from '@core/redux/interface';
import { useTheme } from '@mui/material/styles';
import { useCallback, useMemo } from 'react';

export const useConnect = (scanId: string) => {
  const dispatch = useAppDispatch();
  const initialCameraSet = useSelector(viewer3dSelectors.initialCameraSet);
  const isSelected = useSelector(viewer3dSelectors.scanIdIsSelected(scanId));
  const selectedScanId = useSelector(viewer3dSelectors.selectedScanId);

  // Performance Optimization: Only subscribe to the scan point if this scan is selected.
  // This prevents non-selected scans from re-rendering when the selected scan point changes.
  const scanPoint = useSelector((state: RootState) =>
    isSelected ? viewer3dSelectors.selectScanPoint(state) : undefined,
  );

  const scan = useSelector(scanSelectors.selectById(scanId));
  const theme = useTheme();

  const selectedProcessingResultId = scan?.selectedProcessingResultId;
  const disableUserTransformation = scan?.disableUserTransformation ?? false;
  const processingResult = useSelector(
    processingResultSelectors.selectById(selectedProcessingResultId),
  );
  const texturedMesh = processingResult?.texturedMeshes && processingResult.texturedMeshes[0];

  const userTransformation = useMemo(() => {
    const helperColor = getScanHelperColor(scan, processingResult, theme);

    if (!scan) {
      return {
        helperColor,
        position: new Vector3(),
        rotation: new Euler(),
      };
    }

    const offset = texturedMesh?.mesh.offset;
    const autoTranslation = processingResult?.autoAlignmentTranslation;
    const autoRotation = processingResult?.autoAlignmentRotation;
    const userTranslation = processingResult?.userTranslation;
    const userRotation = processingResult?.userRotation;
    const disableAutoAlignedPosition = scan.disableAutoAlignedPosition;

    // Show auto alignment transformation
    if (disableUserTransformation && !(disableAutoAlignedPosition ?? true)) {
      return {
        helperColor,
        position: jsonToVector(autoTranslation || offset),
        rotation: jsonToEuler(autoRotation),
      };
    }

    // Show user transformation
    if (!disableUserTransformation && (disableAutoAlignedPosition ?? true)) {
      return {
        helperColor,
        position: jsonToVector(userTranslation || offset),
        rotation: jsonToEuler(userRotation),
      };
    }

    // No transformation (both disabled)
    return {
      helperColor,
      position: jsonToVector(offset),
      rotation: new Euler(),
    };
  }, [scan, disableUserTransformation, processingResult, texturedMesh?.mesh.offset, theme]);

  const onScanLoadedToScene = useCallback(() => {
    dispatch(
      scanActions.updateOne({
        id: scanId,
        loaded: true,
      }),
    );
  }, [dispatch, scanId]);

  const moveCameraTo = useCallback(
    ({ x, y, z }: Vector3) =>
      dispatch(
        viewer3dActions.cameraGoto({
          x,
          y,
          z,
          initial: true,
        }),
      ),
    [dispatch],
  );

  return useMemo(
    () => ({
      scan,
      initialCameraSet,
      isSelected,
      selectedScanId,
      userTransformation,
      scanPoint,
      onScanLoadedToScene,
      moveCameraTo,
    }),
    [
      scan,
      initialCameraSet,
      isSelected,
      selectedScanId,
      userTransformation,
      scanPoint,
      onScanLoadedToScene,
      moveCameraTo,
    ],
  );
};
